import { getTheme, Stack } from '@fluentui/react';
import { FluentProvider, SearchBox, Spinner, Text, Tooltip, webLightTheme } from '@fluentui/react-components';
import {
  ChevronDown20Regular,
  ChevronRight20Regular,
  Circle20Filled,
  DataLine20Regular,
  DataUsage20Regular,
  Folder20Regular,
  Home20Filled,
  Home20Regular,
  ShiftsActivity16Regular,
} from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { toJS } from 'mobx';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { injectIntlWithObserver } from '../../../../utils';
import { getShowReportUnsavedDialog, onChangeReportId, onChangeReports } from '../../@data/actions';
import { getPageStore, RouteName } from '../../@data/store/store';
import { ChartGrain } from '../../DashboardCards/ReportCard/ReportCard.types';
import { reportTypeDefaultColumns } from '../../DetailPage/utils/utils';
import { usePubCenterReportTypes, usePublisherProfileDetails, useSavedReports } from '../../hooks/useReportQuery';
import OverviewPageMessages from '../../OverviewPage/OverviewPage.messages';
import { RangeType } from '../CustomCalender/CustomCalender.types';
import { get7DaysExcludingToday } from '../CustomCalender/utils/CalenderUtils';
import { UnsavedReportDialog } from './components/UnsavedReportDialog';
import { getClassNames } from './LeftHandNavigation.styles';
import { DISTRIBUTION_SECTION_ID, IReportSection, MY_REPORT_SECTION_ID, SYNDICATION_SECTION_ID } from './LeftHandNavigation.types';

export const LeftHandNavigation = injectIntlWithObserver((props) => {
  const {
    intl: { formatMessage },
  } = props;

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSection, setExpandedSection] = useState<string>();
  // const [isNavVisible, setIsNavVisible] = useState(true);
  const isNavVisible = true; // FIXME remove this on phase 2
  const [agreeFun, setAgreeFun] = useState<(() => void) | undefined>();
  const classNames = getClassNames(getTheme(), isNavVisible);
  const { reports: mobxReports } = getPageStore();
  const isOverviewSelected = window.location.pathname.split('/').pop() === RouteName.overview;

  const showReportUnsavedDialog = getShowReportUnsavedDialog();

  const reportId = useMemo(
    () => {
      const pathSegments = window.location.pathname.split('/');
      const reportIndex = pathSegments.findIndex((segment) => segment === RouteName.details);
      return reportIndex !== -1 && reportIndex < pathSegments.length - 1 ? pathSegments[reportIndex + 1] : undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [window.location.pathname]
  );
  const queryClient = useQueryClient();

  const reports = toJS(mobxReports);

  const currentReport = useMemo(
    () =>
      reports
        ?.find((section) => section.items.some((item) => item.id.toLowerCase() === reportId?.toLowerCase()))
        ?.items.find((item) => item.id.toLowerCase() === reportId?.toLowerCase()),
    [reportId, reports]
  );

  const { data: reportTypesData, isLoading } = usePubCenterReportTypes();
  const { data: savedReports, isLoading: isLoadingSavedReports } = useSavedReports();
  const { data: publisher } = usePublisherProfileDetails();

  const Last7ExcludingToday = get7DaysExcludingToday();
  const startDate = Last7ExcludingToday[0];
  const endDate = Last7ExcludingToday[Last7ExcludingToday.length - 1];

  const getAvailableReports = () => {
    const preMadeReports: IReportSection[] = [
      {
        id: SYNDICATION_SECTION_ID,
        name: SYNDICATION_SECTION_ID.charAt(0).toUpperCase() + SYNDICATION_SECTION_ID.slice(1),
        items: [],
        icon: <DataUsage20Regular />,
      },
      {
        id: DISTRIBUTION_SECTION_ID,
        name: DISTRIBUTION_SECTION_ID.charAt(0).toUpperCase() + DISTRIBUTION_SECTION_ID.slice(1),
        items: [],
        icon: <DataLine20Regular />,
      },
    ];

    preMadeReports.forEach((section) => {
      section.items =
        reportTypesData
          ?.filter(
            (i) =>
              (section.id === DISTRIBUTION_SECTION_ID && i.id.toLowerCase().includes(section.id)) ||
              (section.id === SYNDICATION_SECTION_ID && !i.id.toLowerCase().includes(DISTRIBUTION_SECTION_ID))
          )
          .map((report) => ({
            ...report,
            reportType: report.id,
            filters: {},
            columns: reportTypeDefaultColumns[report.id.toLowerCase()] || [],
            grain: ChartGrain.DAILY,
            dateRange: { startDate, endDate },
            isSaved: true,
            isScheduled: false,
            rangeType: RangeType.Last7ExcludingToday,
          })) || [];
    });

    preMadeReports.push({
      id: MY_REPORT_SECTION_ID,
      name: 'My Reports',
      items: savedReports || [],
      icon: <Folder20Regular />,
    });
    return preMadeReports.filter((section) => section.items.length > 0 || section.id === MY_REPORT_SECTION_ID);
  };

  useEffect(() => {
    const availableReports = getAvailableReports();
    onChangeReports(availableReports);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedReports, reportTypesData]);

  const filteredReports: IReportSection[] = useMemo(() => {
    if (!searchTerm) {
      return [...reports];
    }
    return [...reports]
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase())),
      }))
      .filter((section) => section.items.length > 0);
  }, [reports, searchTerm]);

  useEffect(() => {
    if (!isOverviewSelected) {
      openSection(reports.find((section) => section.items.some((item) => item.id.toLowerCase() === reportId?.toLowerCase()))?.id || '');
    } else {
      onChangeReportId(undefined, props.history);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, isOverviewSelected, mobxReports]);

  const openSection = (sectionId: string) => {
    setExpandedSection(sectionId);
  };

  const toggleSection = (sectionId: string) => {
    if (sectionId === expandedSection) {
      setExpandedSection(undefined);
      return;
    }
    setExpandedSection(sectionId);
  };

  // const toggleVisibility = () => {
  //   setIsNavVisible((prev) => !prev);
  // };

  const handleItemClick = (itemId?: string) => {
    if (currentReport && !currentReport.isSaved && showReportUnsavedDialog) {
      setAgreeFun(() => () => {
        onChangeReportId(itemId, props.history);
        queryClient.invalidateQueries(['pubCenterSavedReports']);
      });
      return;
    } else if (!showReportUnsavedDialog) {
      queryClient.invalidateQueries(['pubCenterSavedReports']);
    }
    onChangeReportId(itemId, props.history);
  };

  const renderReportSection = (section: IReportSection) => {
    const isExpanded = expandedSection === section.id;

    return (
      <div key={section.id} aria-label={section.id}>
        <div
          className={classNames.sectionHeader}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection(section.id)}
          onClick={() => toggleSection(section.id)}
          role="button"
          tabIndex={0}
        >
          {React.isValidElement(section.icon)
            ? React.cloneElement(section.icon as React.ReactElement, { className: classNames.icon })
            : null}
          <Text>{section.name}</Text>
          {isExpanded ? (
            <ChevronDown20Regular className={classNames.chevronIcon} />
          ) : (
            <ChevronRight20Regular className={classNames.chevronIcon} />
          )}
        </div>

        {isExpanded &&
          section.items.map((item) => (
            <div
              key={item.id}
              className={classNames.navItem}
              onKeyDown={(e) => e.key === 'Enter' && handleItemClick(item.id)}
              onClick={() => handleItemClick(item.id)}
              role="button"
              tabIndex={0}
            >
              {reportId?.toLowerCase() === item.id.toLowerCase() && <div className={classNames.selectedBar} />}
              {!item.isSaved ? <Circle20Filled className={classNames.circleIcon} /> : <div className={classNames.iconEmpty} />}
              <Tooltip key={item.id} content={item.name} relationship="description">
                <Text
                  className={classNames.itemName}
                  style={{
                    fontWeight: reportId?.toLowerCase() === item.id.toLowerCase() ? 'bold' : undefined,
                  }}
                >
                  {item.name}
                </Text>
              </Tooltip>
              {item.isScheduled && (
                <Tooltip content="Scheduled" key={'scheduled'} relationship="description">
                  <ShiftsActivity16Regular className={classNames.scheduleIcon} />
                </Tooltip>
              )}
            </div>
          ))}
      </div>
    );
  };

  return (
    <FluentProvider theme={webLightTheme}>
      {showReportUnsavedDialog && agreeFun && (
        <UnsavedReportDialog
          isOpen={true}
          setIsOpen={(isOpen) => {
            if (!isOpen) {
              setAgreeFun(undefined);
            }
          }}
          onAgree={() => {
            agreeFun();
            setAgreeFun(undefined);
          }}
        />
      )}

      <Stack className={classNames.root}>
        {/* <Navigation20Regular onClick={toggleVisibility} className={classNames.navigationIcon} /> */}
        {isNavVisible && (
          <>
            {isLoading || !publisher || isLoadingSavedReports ? (
              <Spinner style={{ top: '50%', left: '50%', position: 'absolute' }} />
            ) : (
              <>
                <SearchBox
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(_, newValue) => setSearchTerm(newValue.value || '')}
                  className={classNames.searchBox}
                />
                {(publisher.allowSyndicationReporting ||
                  publisher.allowDistributionReporting ||
                  publisher.allowDistributionV2Reporting) && (
                  <div
                    className={classNames.sectionHeader}
                    onKeyDown={(e) => e.key === 'Enter' && handleItemClick()}
                    onClick={() => handleItemClick()}
                    role="button"
                    tabIndex={0}
                  >
                    {isOverviewSelected && <div className={classNames.selectedBar} />}
                    {isOverviewSelected ? (
                      <Home20Filled className={classNames.icon} color="blue" />
                    ) : (
                      <Home20Regular className={classNames.icon} />
                    )}
                    <Text style={{ fontWeight: isOverviewSelected ? 'bold' : undefined }}>{formatMessage(OverviewPageMessages.title)}</Text>
                  </div>
                )}
                {filteredReports.map((section) => renderReportSection(section))}
              </>
            )}
          </>
        )}
      </Stack>
    </FluentProvider>
  );
});
